/**
 * wc77-flow-b.js — Flow B ("watercolor" arm) on-page customize flow.
 * 2026-07-01 — flow-ui-spec §Flow B (family-present layout, Wolden style).
 *
 * Booted ONLY via dynamic import from wc77-init.js when
 * window.WC_FLOW.arm === "watercolor" (other arms never fetch this file) —
 * OR, regardless of arm, when the page carries a data-wc-standalone flow-b
 * root (the standalone watercolor-with-name page, templates/
 * product.watercolor-name.json — outside the flow test entirely; see
 * flow-ui-spec §Standalone; standalone deltas: name ALWAYS on, Canvas +
 * Framed only, orchestrator payload skipProducts:["pbn"]).
 * Markup: snippets/wolden-flow-b.liquid (+ the "adjust-photo" modal section
 * in snippets/wolden-customize-modal.liquid). Styles: wolden-flow-b.css.
 *
 * REUSE MAP (spec §Common 4 "reuse, do not fork"):
 *   - Validation + presign → S3 PUT + orchestrator plumbing + payload base +
 *     dropped-connection recovery: imported from wc77-upload.js (exported
 *     seams added 2026-07-01 — behavior identical for the modal flow).
 *   - Pickers: the SAME pdp-options snippet + the document-level selector
 *     delegation in wc77-selectors.js drives Flow B's copy untouched; this
 *     module injects per-size prices ON THE STANDALONE PAGE ONLY (from the
 *     same JS pricing constants — no fourth pricing copy; the TEST arm
 *     mirrors the champion modal's single price line — 2026-07-02
 *     apples-to-apples residuals) and hydrates R post-generation.
 *     2026-07-02 (scope correction item 2, apples-to-apples): the copy is
 *     modal-identical — 2 format tiles (Canvas Print / Paint Kit) with the
 *     Frame row + Frame Color NESTED inside Canvas, injected by the shared
 *     Version D projection (renderVersionDOnPdpOptions now covers flow
 *     copies too). Default selection: 8x10 Canvas Print, unframed.
 *   - ATC: native PDP form. updatePdpVariantState() writes the child-product
 *     variant id + the eight properties[_*] inputs; this module adds
 *     _pet_name through the same writeFormProperty() seam.
 *   - Crop dialog: a new state in the EXISTING modal state machine
 *     (wc2-modal.js) — focus trap, ESC, backdrop come free.
 *   - localStorage persistence: wc2-state add()/read() with the exact
 *     snapshot shape reopenFromStateB/initUploadZoneState already read.
 *   - Inline ticker: a CLONE of the wc77-loading pattern (sanctioned by the
 *     code map §5.1) because wc77-loading writes into modal elements and
 *     flips modal state — Flow B's progress lives inline on the page.
 *
 * NAME → PRINT TIMING (locked Lambda contract): the server composites the
 * pet name onto the print file AT GENERATION TIME (NBP data[3]); therefore
 * the name value present at crop DONE is what rides the payload as petName.
 * The name input is deliberately fillable before upload; post-generation
 * edits update the live client overlay + the _pet_name cart property only.
 *
 * OVERLAY GEOMETRY (must match lambdas/badass-nanobanana-pro/handler.js —
 * change BOTH or preview ≠ print):
 *   sanitize: collapse whitespace, trim, max 30 chars
 *   fontSize = width × 0.10 × min(1, 12 / name.length)
 *   anchor: horizontally centered, alphabetic BASELINE at y = height × 0.85
 *   draws: black #000 at 35% opacity offset DOWN by fontSize × 0.04 (min
 *          1px), then white #FFF on top. NO blur (rasterizers differ).
 *   font: 'WC Great Vibes' — same TTF the Lambda ships (@font-face in the
 *         flow-b snippet points at assets/wolden-great-vibes.ttf).
 */

import { log, track } from './wc77-analytics.js';
import { isEmailCaptured, captureEmail, getCapturedEmail } from './wc2-session.js';
import {
  getNamespaceKey,
  getNamespaceType,
  read,
  add,
  updateMockups,
  tempGenerationId,
} from './wc2-state.js';
import { getModal, openModal, closeModal } from './wc2-modal.js';
import {
  isAcceptedFile,
  requestPresignedUrl,
  uploadToS3,
  buildOrchestratorPayload,
  runFullGeneration,
  pollFetchGen,
} from './wc77-upload.js';
import { mapFetchGenToOrchResult } from './wc2-recovery-map.js?v=2';
import {
  updatePdpVariantState,
  applyPdpVisibility,
  writeFormProperty,
  getCurrentPricing,
  refreshActiveBranches,
  refreshSizeFramePills,
  renderUnifiedGallery,
  CANVAS_PRICING,
  CANVAS_FRAMED_PRICING,
  PAINT_KIT_PRICING,
} from './wc77-selectors.js';
// 2026-07-02 QA round-4 — Phase-2 hydration parity with the champion flow
// (wc77-upload.js onPhase2 → updateResultPhase2). Flow B previously only
// track()'d the phase-2 payload and threw it away.
import { updateResultPhase2 } from './wc77-result.js';

// ─── Module state ─────────────────────────────────────────────────────────────

var _root = null;
var _els = {};                 // cached [data-wc-flow-b=...] elements
var _standalone = false;       // standalone watercolor page (flow-ui-spec
                               // §Standalone): root carries data-wc-standalone,
                               // rendered OUTSIDE the flow test — name always
                               // on, Canvas+Framed only, skipProducts:["pbn"]
var _dynamicDates = false;     // 2026-07-10 (Joe) — VERSION B of the dynamic
                               // page (root carries data-wc-art-text-dates,
                               // from metafield custom.art_text_dates): name
                               // REQUIRED + optional memorial dates painted
                               // beneath it. Same page, same code — this
                               // flag IS the version.
var _dynamicText = false;      // 2026-07-09 dynamic-name — standalone page
                               // whose root carries data-wc-art-text-dynamic
                               // (product metafield custom.art_text_dynamic):
                               // the AI repaints the art's painted text to
                               // the typed name (or removes it) via a prompt
                               // appendix + ar45, instead of the fixed-font
                               // petName/data[3] composite. EVERY dynamic
                               // behavior below gates on this; false = the
                               // pre-dynamic behavior, byte-identical.
var _nameEnabled = false;      // standalone only (2026-07-02 strip: the test
                               // arm renders no name UI; the dormant
                               // sub === 'name_on' check remains for a
                               // future sub-variant test)
var _petNameVisible = true;    // Show/Hide toggle state
var _sourceCanvas = null;      // EXIF-normalized photo, ≤2048px long edge
var _sourceName = '';          // original filename (for hints + upload name)
var _crop = null;              // {x,y,w,h} in SOURCE-canvas pixels
var _cropDefaultSide = 0;      // for the crop_confirmed zoom factor
var _cropMoved = false;
var _stageScale = 1;           // stage px per source px
var _croppedFile = null;       // waiting on the email gate
var _genInFlight = false;
var _recovering = false;
var _completedGenId = null;    // double-hydrate guard (orchestrator vs recovery race)
var _lastPublicUrl = null;     // presigner publicUrl of the active upload
var _petNameSentAtGeneration = ''; // what the server composited (report/debug)

var PENDING_KEY = 'wc_flowb_pending'; // OWN key — the shared modal recovery
                                      // (wc_pending_gen) must not hydrate a
                                      // Flow B gen into the reveal modal.

// ─── Small helpers ────────────────────────────────────────────────────────────

function el(name) { return _els[name] || (_els[name] = _root.querySelector('[data-wc-flow-b="' + name + '"]')); }

// source_flow stamp for every Flow B analytics event. Standalone gens must be
// separable from watercolor TEST-ARM gens in every dashboard (the standalone
// page is outside the A/B/C test), so it reports its own label.
function sourceFlow() { return _standalone ? 'watercolor_standalone' : 'watercolor'; }
function modalEl(name) { var m = getModal(); return m ? m.querySelector('[data-wc-flow-b="' + name + '"]') : null; }
function money(n) { return '$' + Number(n).toFixed(2); }

// Lambda contract sanitization — identical in NBP + orchestrator.
function sanitizeName(raw) {
  return String(raw || '').replace(/\s+/g, ' ').trim().slice(0, 30);
}

// 2026-07-09 dynamic-name — the AI-prompt flavor of the name sanitizer:
// the same collapse/trim/30 as sanitizeName PLUS strip double-quotes and
// backslashes. WHY: the name is embedded inside a quoted literal in the
// prompt appendix below — either character would break the quoting or let
// typed text read as prompt instructions.
function promptNameForAppendix(raw) {
  return sanitizeName(raw).replace(/["\\]/g, '');
}

// 2026-07-09 dynamic-name — the prompt appendix dynamic pages ride INSTEAD
// of petName/data[3] (spec R3d): with a name, repaint the art's painted
// text to exactly the typed name in the same script; with no name (the
// "No name" opt-out), remove the painted text entirely. The wording is the
// locked spec text — don't paraphrase it (QA greps image-lambda logs for it).
function buildDynamicPromptAppendix(name, dates) {
  if (!name) {
    return ' Finally, remove all hand-painted text from image 1 completely, filling the space seamlessly with the surrounding background style.';
  }
  // 2026-07-10 (Joe) — VERSION B source art carries TWO sample text lines
  // (large name + smaller dates beneath, often with small decorative
  // ornaments). REPLACING words inside lines the model can already see is
  // the minimal-change edit — far more reliable than asking it to ADD a
  // line. Content rule for B art: bake the sample dates in EXACTLY the
  // format this page paints ("Mar 8, 2014 – May 20, 2026", en dash) so the
  // swap is near character-for-character. No-dates customers get the sample
  // dates line REMOVED (never ship placeholder dates).
  if (_dynamicDates) {
    // WITH dates — byte-identical to the QA-verified wording (2026-07-10).
    if (dates) {
      return ' Finally, image 1 contains two lines of text near the bottom: a large name line, and a smaller dates line beneath it. Keep both lines exactly where they are, in exactly the same font, style, color and size, and keep any small decorative ornaments beside them. Replace the words of the large name line with exactly: "' + name + '". Spell it exactly as written, letter for letter.' +
             ' Replace the words of the smaller dates line with exactly: "' + dates + '". Copy it character for character, including the dash.';
    }
    // NO dates (2026-07-12, Joe — probe-verified): the old wording removed
    // the dates line but left the name PARKED at its original height, so a
    // big empty cloud band gaped underneath (live customer "Yogi"). This
    // wording drops the keep-in-place anchor and re-seats the single name
    // line low in the cloud area the two lines used to share.
    // 2026-07-13 (Joe): a touch lower than dead-center — 20% of the name's
    // height further down (name center ~85% of canvas; below-strip about
    // three quarters of the name's height). Probe-verified.
    return ' Finally, image 1 contains two lines of text near the bottom: a large name line, and a smaller dates line beneath it. Replace the words of the large name line with exactly: "' + name + '". Spell it exactly as written, letter for letter, keeping the same font, style, color and size. Remove the smaller dates line and its ornaments completely, filling the space seamlessly with the surrounding background, and move the name line lower so it sits just below the center of the cloud area the two text lines used to share — the strip of cloud beneath the name about three quarters of the name\'s height.';
  }
  // VERSION A — single sample text line (unchanged wording, QA-verified).
  var a = ' Finally, image 1 contains hand-painted text. Repaint that text in exactly the same font, style, color, size and position, but change the words to exactly: "' + name + '". Spell it exactly as written, letter for letter, with no extra words.';
  if (dates) a += ' Directly below that text, add one more line of smaller hand-painted text in exactly the same style and color, reading exactly: "' + dates + '". Copy it character for character, including the dash.';
  return a;
}

// ── 2026-07-10 (Joe) — VERSION B date helpers ──
// Native date inputs give "yyyy-mm-dd"; parse by PARTS (never new Date(str)
// — timezone parsing shifts the day for some locales). Painted formats:
//   default              "Feb 14, 2012"      (day month year)
//   month & year only    "Feb 2012"
// Two dates join with an en dash; one date paints alone.
var MONTHS_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatOneDate(iso, monthYearOnly) {
  // 2026-07-10 round-2 — day part now OPTIONAL: "Month & year only" swaps the
  // native control to type="month", whose values are "yyyy-mm" (no day).
  var m = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(String(iso || ''));
  if (!m) return '';
  var month = MONTHS_ABBR[parseInt(m[2], 10) - 1];
  if (!month) return '';
  if (monthYearOnly || !m[3]) return month + ' ' + m[1];
  return month + ' ' + parseInt(m[3], 10) + ', ' + m[1];
}

// ── 2026-07-10 round-2 (Joe) — date-picker UX helpers ──

// <input type="month"> support probe (iOS/Android/Chrome yes; Firefox and
// desktop Safari keep the day picker — the PAINTED string is month-year
// either way once the shortener is checked, so degradation is cosmetic).
var _monthInputSupport = null;
function monthInputSupported() {
  if (_monthInputSupport === null) {
    var probe = document.createElement('input');
    try { probe.type = 'month'; } catch (e) { /* older engines throw */ }
    _monthInputSupport = (probe.type === 'month');
  }
  return _monthInputSupport;
}

function monthYearChecked() { return !!(el('dates-monthyear') && el('dates-monthyear').checked); }

// The suggested starter dates (Joe: the picker should OPEN somewhere sane,
// not at today-for-both): "got them" → Jan 1 2016, "passed" → today. Client
// clock on purpose — a Liquid-baked today goes stale in the page cache.
function defaultDateValue(inputEl, which) {
  var monthMode = inputEl && inputEl.type === 'month';
  if (which === 'from') return monthMode ? '2016-01' : '2016-01-01';
  var d = new Date();
  var mm = ('0' + (d.getMonth() + 1)).slice(-2);
  return monthMode ? (d.getFullYear() + '-' + mm)
                   : (d.getFullYear() + '-' + mm + '-' + ('0' + d.getDate()).slice(-2));
}

// Keep the gray in-field starter text truthful: hidden the moment a field
// holds a value (native date inputs support no placeholder attribute, so a
// pointer-events:none span overlays the empty control — wolden-flow-b.css).
function syncDateFieldUI() {
  if (!_dynamicDates) return;
  ['date-from', 'date-to'].forEach(function (k) {
    var f = el(k);
    if (!f) return;
    var field = f.closest('.wolden-flow-b__dates-field');
    if (field) field.classList.toggle('wolden-flow-b__dates-field--filled', !!f.value);
    var ph = el(k + '-ph');
    if (ph) ph.textContent = formatOneDate(defaultDateValue(f, k === 'date-from' ? 'from' : 'to'), monthYearChecked());
  });
}

// "Month & year only" flips the NATIVE control too (Joe, round-2 items 4/5):
// checked → type="month" (iOS opens the month/year wheel), unchecked →
// type="date" (day calendar). Values convert both ways; coming back to day
// mode stands the 1st in as the day — visible in the field, never hidden.
function applyMonthYearMode() {
  if (!_dynamicDates || !monthInputSupported()) return;
  var monthOnly = monthYearChecked();
  ['date-from', 'date-to'].forEach(function (k) {
    var f = el(k);
    if (!f) return;
    var v = f.value;
    if (monthOnly && f.type !== 'month') {
      // Adversarial-review fix (2026-07-10): STASH the picked day before the
      // month value discards it — without this, check-then-uncheck silently
      // rewrote "Jun 3, 2024" to "Jun 1, 2024", and a wrong day would have
      // been PAINTED onto a memorial canvas.
      if (v && v.length === 10) f.setAttribute('data-wc-day-stash', v.slice(8, 10));
      f.type = 'month';
      if (v) f.value = v.slice(0, 7);
    } else if (!monthOnly && f.type !== 'date') {
      var stash = f.getAttribute('data-wc-day-stash');
      f.type = 'date';
      if (v) {
        if (v.length === 7) {
          f.value = v + '-' + (stash || '01');
          // A stashed day the (possibly re-picked) month doesn't have — e.g.
          // the 31st restored into February — makes the browser blank the
          // field; fall back to the 1st rather than lose the whole date.
          if (!f.value) f.value = v + '-01';
        } else {
          f.value = v;
        }
      }
    }
  });
}

// How many of the two date fields hold a value — updateStepFlow keeps the
// step marker ON the Dates card until both are set (round-2 item 3: one
// filled date means they're mid-task; moving the marker on was premature).
function filledDateCount() {
  if (!_dynamicDates) return 0;
  return ['date-from', 'date-to'].reduce(function (n, k) {
    return n + (((el(k) || {}).value) ? 1 : 0);
  }, 0);
}

// 2026-07-10 — a date field holding text we CAN'T read as a date. Only
// possible on browsers without native date inputs (the field degrades to
// free text there); a native picker's value is always '' or valid ISO.
// Non-empty + unparseable = the customer clearly WANTED dates → block with
// a message rather than silently painting none.
function datesInputInvalid() {
  if (!_dynamicDates) return false;
  return ['date-from', 'date-to'].some(function (k) {
    var v = String(((el(k) || {}).value) || '').trim();
    // yyyy-mm-dd (date input) or yyyy-mm (month input) both read fine.
    return v !== '' && !/^\d{4}-\d{2}(-\d{2})?$/.test(v);
  });
}

function showDatesError(show) {
  var err = el('dates-error');
  if (err) err.hidden = !show;
  if (show) {
    var block = el('dates-block');
    if (block) { try { block.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { /* ignore */ } }
  }
}

// The full painted dates string from the two inputs + the shortener toggle.
// '' when neither date is set (the appendix then paints the name only).
function paintedDatesString() {
  if (!_dynamicDates) return '';
  var monthYearOnly = !!(el('dates-monthyear') && el('dates-monthyear').checked);
  var from = formatOneDate((el('date-from') || {}).value, monthYearOnly);
  var to = formatOneDate((el('date-to') || {}).value, monthYearOnly);
  if (from && to) return from + ' – ' + to;
  return from || to || '';
}

function validEmail(v) {
  // Deliberately loose (the orchestrator/Klaviyo re-validate); blocks only
  // obvious non-emails so the gate never strands a real customer.
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v || '').trim());
}

function showError(msg) {
  var e = el('error');
  if (!e) return;
  e.textContent = msg || '';
  e.hidden = !msg;
}
function clearError() { showError(''); }

// 2026-07-09 v2 (Joe) — NOTHING auto-starts a generation on the standalone
// page anymore. The earlier auto-resume (checking "No name" kicked off the
// art) read as the page acting on its own; the customer must press
// Start My Art. In its place:
//   _preupload — the cropped photo is SILENTLY uploaded in the background
//   right after crop DONE (no progress bar), so the button feels instant.
//   {file, promise->presignerResp|null}. A failed pre-upload resolves null
//   and startGeneration simply uploads normally at click time.
var _preupload = null;

function preuploadCropped(file) {
  var pid = tempGenerationId(); // its own id — the photo URL is what matters,
                                // not the id it was presigned under
  var p = requestPresignedUrl(pid, file)
    .then(function (resp) {
      return uploadToS3(resp.uploadUrl, file).then(function () { return resp; });
    })
    ['catch'](function (err) {
      log('[flow-b] silent pre-upload failed (will upload at Start My Art)', err && err.message);
      return null;
    });
  _preupload = { file: file, promise: p };
}

// The step-2 requirement on dynamic pages: a name, or the explicit opt-out.
// Non-dynamic pages have no requirement (nameless generations are valid).
function nameRequirementMet() {
  if (!_dynamicText) return true;
  // 2026-07-10 (Joe) — VERSION B (dates page): the name is strictly
  // required; the "No name" opt-out doesn't render there.
  if (_dynamicDates) return !!sanitizeName((el('name-input') || {}).value);
  var box = el('noname-checkbox');
  return !!sanitizeName((el('name-input') || {}).value) || !!(box && box.checked);
}

// Walk the eye to the current step's element (the "Next" chip + orange
// marker land there; the scroll makes sure they're on screen).
function scrollToStepTarget() {
  if (!_standalone || !_root) return;
  var step = _root.getAttribute('data-wc-flow-b-step');
  var target = (step === 'name') ? el('name-block')
             : (step === 'dates') ? el('dates-block')
             : (step === 'start') ? _root.querySelector('.wolden-flow-b__actions')
             : null;
  if (!target) return;
  try { target.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { /* ignore */ }
}

// 2026-07-09 dynamic-name — inline required-name error (the Liquid renders
// the element ONLY on dynamic pages, so this no-ops everywhere else). On
// show, also walk the customer to the name block: the gate fires on the
// crop-DONE path, and the name block may be scrolled off-screen by then.
function showNameError(show) {
  var err = el('name-error');
  if (err) err.hidden = !show;
  if (show) {
    var block = el('name-block');
    if (block) { try { block.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { /* ignore */ } }
  }
}

function setFileHint(msg) {
  var h = el('file-hint');
  if (h) h.textContent = msg;
}

function productCtxFromRoot() {
  return {
    productId:        _root.getAttribute('data-wc-product-id'),
    productHandle:    _root.getAttribute('data-wc-product-handle'),
    productType:      _root.getAttribute('data-wc-product-type'),
    originalImageUrl: _root.getAttribute('data-wc-original-image-url'),
    prompt:           _root.getAttribute('data-wc-prompt'),
    artId:            _root.getAttribute('data-wc-product-art-id') || null,
  };
}

function productLite() {
  var c = productCtxFromRoot();
  return { productHandle: c.productHandle, productType: c.productType, artId: c.artId };
}

// ─── R (WC._currentResult) seeding + hydration ───────────────────────────────

function ensureR() {
  var wc = (window.WC = window.WC || {});
  var R = (wc._currentResult = wc._currentResult || {});
  return R;
}

function checkedValue(action) {
  var input = _root.querySelector('input[data-wc-action="' + action + '"]:checked');
  return input ? input.value : null;
}

// Seed picker state pre-generation so the shared select-* handlers (which
// read/write R.active*) work from the first click — Flow B shows pickers
// BEFORE a generation exists, unlike the current flow.
function seedPickerState() {
  var R = ensureR();
  var srcFormat = (_root.querySelector('[data-wc-component="pdp-options"]') || _root).getAttribute('data-wc-source-format');
  if (!R.activeFormat)            R.activeFormat            = checkedValue('select-format') || srcFormat || 'canvas';
  // 2026-07-02 (nested-frame pickers) — the shared Version D projection
  // renames the canvas size action select-canvas-size → select-canvas-size-d
  // when it injects the Frame row, and its boot order vs this module is not
  // guaranteed; read BOTH names so a URL-derived child-PDP size (e.g. a
  // shared 16x20 link) survives whichever runs first.
  if (!R.activeCanvasSize)        R.activeCanvasSize        = checkedValue('select-canvas-size') || checkedValue('select-canvas-size-d') || '8x10';
  if (!R.activeCanvasFramedSize)  R.activeCanvasFramedSize  = checkedValue('select-canvas-framed-size') || '8x10';
  if (!R.activeCanvasFramedColor) R.activeCanvasFramedColor = checkedValue('select-canvas-framed-color') || 'White';
  if (!R.activePaintKitSize)      R.activePaintKitSize      = checkedValue('select-paintkit-size') || '16x20';
  if (!R.activePaintKitFrame)     R.activePaintKitFrame     = checkedValue('select-paintkit-frame') || 'No Frame';
}

// Mirror of showResult's orchResult→R mapping (wc77-result.js:516-581) minus
// every modal render. Kept field-for-field so the native-form ATC path
// (updatePdpVariantState) and the localStorage snapshot are byte-compatible
// with gens made through the modal flow.
function hydrateR(orchResult) {
  var R = ensureR();
  R.generationId = orchResult.generationId;
  R.bubbleId = orchResult.bubbleId || null;
  R.canvasMockups = (orchResult.mockups && orchResult.mockups.canvas) || null;
  var framedMockups = (orchResult.mockups && orchResult.mockups.framed) ||
                      (orchResult.canvasFramed && orchResult.canvasFramed.mockups) || null;
  if (framedMockups) R.canvasFramedMockups = framedMockups;
  R.paintKitMockup = (orchResult.mockups && orchResult.mockups.paintKit) || null;
  R.pbnBwUrl = (orchResult.mockups && orchResult.mockups.pbnBw) || null;
  R.pbnColoredUrl = (orchResult.mockups && orchResult.mockups.pbnColored) || null;
  var orchCanvasShopify = orchResult.canvas && orchResult.canvas.shopify;
  var orchFramedShopify = orchResult.canvasFramed && orchResult.canvasFramed.shopify;
  if (orchCanvasShopify) R.canvasShopify = orchCanvasShopify;
  if (orchFramedShopify) R.canvasFramedShopify = orchFramedShopify;
  R.paintKitShopify = orchResult.paintKit || R.paintKitShopify || null;
  // 2026-07-02 QA round-4 — recovery/reload paths (fetch-gen shape) carry the
  // straight-on front shot inline; map it like the champion's showResult does
  // (wc77-result.js:558) so the canvas deck's 3rd slot hydrates immediately.
  if (orchResult.straightOn && orchResult.straightOn.url) R.straightOnMockupUrl = orchResult.straightOn.url;
  var data = orchResult.data || [];
  R.artDisplayUrl = (data[1] && data[1].s3_url) || (data[0] && data[0].s3_url) ||
                    (data[1] && data[1].display_url) || (data[0] && data[0].display_url) || '';
  R.artHighResUrl = (data[0] && data[0].s3_url) || (data[0] && data[0].display_url) || R.artDisplayUrl || '';
  if (_lastPublicUrl) R.petImageUrl = _lastPublicUrl;
  R.productCtx = productCtxFromRoot();
  seedPickerState();
  return R;
}

// ─── Straight-on front-shot poll (champion parity; QA round-4) ────────────────
// Phase-1 answers straightOnPending:true for EVERY gen now (the orchestrator
// generates the Printify front shot for all traffic — the champion's "canary
// only" comment predates this). The champion polls fetch-gen until
// straightOn.ready and re-renders the canvas deck's 3rd slot; the flows were
// missing that poll, so their canvas deck never gained the straight-on slide.
// Reuses pollFetchGen with attempts=1 (single fetch-gen probe per tick).
// Exported so wc77-flow-c.js can share it (it already imports from here).
var _straightOnTimer = null;
export function startStraightOnPoll(generationId, onReady, logLabel) {
  if (_straightOnTimer) return;
  var t0 = Date.now();
  _straightOnTimer = setInterval(function () {
    var R = (window.WC && window.WC._currentResult) || {};
    // Stop when: timed out (2 min, matching the champion), the customer moved
    // to a different gen, or the slide already landed via another path.
    if (Date.now() - t0 > 120000 || R.generationId !== generationId || R.straightOnMockupUrl) {
      clearInterval(_straightOnTimer);
      _straightOnTimer = null;
      return;
    }
    pollFetchGen(generationId, 1, 0).then(function (out) {
      var so = out && out.body && out.body.straightOn;
      if (so && so.ready && so.url && _straightOnTimer) {
        clearInterval(_straightOnTimer);
        _straightOnTimer = null;
        log('[' + (logLabel || 'flow') + '] straight-on ready', so.url);
        try { onReady(so.url); } catch (e) { log('straight-on onReady error', e && e.message); }
      }
    });
  }, 5000);
}

// Flow B's onReady: hydrate R, repaint the on-page gallery, persist the URL
// into the saved snapshot (reload keeps the 3-slot deck).
function applyStraightOn(url) {
  var R = ensureR();
  R.straightOnMockupUrl = url;
  try { renderUnifiedGallery(); } catch (e) { /* non-fatal */ }
  try { updateMockups(R.generationId, { straightOn: url }); } catch (e) { /* non-fatal */ }
}

// Persist in the EXACT gen-store shape initUploadZoneState/reopenFromStateB
// write/read, so a Flow B gen survives reloads and stays readable if the
// visitor is ever re-assigned or the arm is promoted.
function persistGen(R) {
  try {
    var p = productLite();
    var key = getNamespaceKey(p);
    if (!key) return;
    add(key, getNamespaceType(p), p.productHandle, p.productType, {
      id:          R.generationId,
      bubbleId:    R.bubbleId || null,
      imageUrl:    R.artDisplayUrl,
      petPhotoUrl: R.petImageUrl || null,
      snapshot: JSON.parse(JSON.stringify({
        canvasMockups:       R.canvasMockups,
        paintKitMockup:      R.paintKitMockup,
        pbnBwUrl:            R.pbnBwUrl,
        pbnColoredUrl:       R.pbnColoredUrl,
        canvasShopify:       R.canvasShopify,
        canvasFramedShopify: R.canvasFramedShopify,
        paintKitShopify:     R.paintKitShopify,
        // 2026-07-02 QA round-3 (round-1 #10) -- snapshot the framed /
        // straight-on mockups so a rehydrated reveal rebuilds the SAME
        // slide decks as a fresh generation (kept in lockstep with the
        // champion persistCurrentGen snapshot shape).
        canvasFramedMockups: R.canvasFramedMockups,
        straightOnMockupUrl: R.straightOnMockupUrl,
      })),
    });
  } catch (e) { log('[flow-b] persistGen failed', e && e.message); }
}

// ─── Per-size price injection (spec §Flow B item 1) ──────────────────────────
// Prices come from the SAME wc77-selectors constants the reveal uses — the
// pricing-truth triple (JS constants / Lambda variants / price.liquid) does
// NOT gain a fourth copy (code-map §2.6 risk).
//
// 2026-07-02 (apples-to-apples residuals) — STANDALONE ONLY. The champion
// modal's size pills carry no prices (one live price line near ATC does the
// talking), so the test arm's pills may not either; only the standalone
// watercolor page (outside the test, reference design has them) calls these.
// The init gate lives in initFlowB — the functions themselves stay mode-blind.

// 2026-07-09 UI pass (Joe) — the per-size pill prices were REMOVED. The
// standalone pills must be pixel-identical to every other customizable PDP
// (bare native pills; the live price paints on the PDP price row through
// updatePdpVariantState, exactly like the champion). The old injectors
// (canvasModePrice / injectSizePrices / refreshCanvasSizePrices, added
// 2026-07-02) are gone — recover from git history if per-pill prices ever
// come back as a test.

// ─── Guided step flow (2026-07-09 UI pass, STANDALONE only) ──────────────────
// One orange "you are here" marker walks the customer down the column:
//   'upload'     no photo yet — pickers hidden, upload card wears the orange
//                dashed treatment, "Start Here" pill points at it
//   'start'      photo staged — upload card grays + shows the file chip (✕
//                removable), the orange ring moves to the Start My Art CTA
//   'generating' progress bar owns the moment — CTA hidden, chip locked
//   'done'       art landed — pickers appear (pick size AFTER seeing the
//                art, like the champion), CTA relabels to View My Art
// The root's data-wc-flow-b-step attribute is the single source of truth;
// all visuals hang off it in wolden-flow-b.css. Everything gates on
// _standalone so the watercolor TEST arm stays byte-identical.
function updateStepFlow() {
  if (!_standalone || !_root) return;
  var step = 'upload';
  if (_completedGenId) step = 'done';
  else if (_genInFlight) step = 'generating';
  // 2026-07-09 v2 (Joe) — photo staged splits into TWO steps: 'name' (the
  // orange marker + "Next" chip sit on the Name card until a name is typed
  // or "No name" is checked) then 'start' (marker + chip move onto the
  // Start My Art button, which the customer must press — no auto-start).
  // 2026-07-10 (Joe, version B) — the marker VISITS the optional Dates card
  // after the name is in. Round-2 item 3: it moves onto the button only when
  // BOTH dates are set — one filled date means they're mid-task, and the
  // marker jumping ahead read as "you're done here" when they weren't.
  // Dates never gate anything: Start My Art works from step 'dates' too.
  else if (_sourceCanvas) {
    if (!nameRequirementMet()) step = 'name';
    else if (_dynamicDates && filledDateCount() < 2) step = 'dates';
    else step = 'start';
  }
  _root.setAttribute('data-wc-flow-b-step', step);
  // The shipping-row "Start Here" pill is outside this root; a body-level
  // copy of the step lets CSS hide it once the flow has moved past step 1.
  try { document.body.setAttribute('data-wc-flowb-step', step); } catch (e) { /* ignore */ }
  var chip = el('file-chip');
  if (chip) chip.hidden = (step === 'upload');
  var rm = el('file-chip-remove');
  // ✕ only while the photo is still swappable-by-removal; post-gen swaps go
  // through Change Photo, and mid-generation nothing is cancellable.
  if (rm) rm.hidden = (step === 'generating' || step === 'done');
  var btn = el('preview-btn');
  if (btn) btn.textContent = (step === 'done') ? 'View My Art' : 'Start My Art';
}

// Fill the staged-photo chip: 112px thumbnail drawn from the decoded source
// (same downscale trick as the wizard's HEIC path) + the original filename.
function renderFileChip() {
  if (!_standalone || !_sourceCanvas) return;
  var img = el('file-chip-img');
  var name = el('file-chip-name');
  if (name) name.textContent = _sourceName || 'photo';
  if (img) {
    try {
      var t = document.createElement('canvas');
      var scale = 112 / Math.max(_sourceCanvas.width, _sourceCanvas.height);
      t.width = Math.max(1, Math.round(_sourceCanvas.width * scale));
      t.height = Math.max(1, Math.round(_sourceCanvas.height * scale));
      t.getContext('2d').drawImage(_sourceCanvas, 0, 0, t.width, t.height);
      img.src = t.toDataURL('image/jpeg', 0.8);
    } catch (e) { log('[flow-b] chip thumb failed', e && e.message); }
  }
}

// ✕ on the chip — forget the staged photo and walk the flow back to step 1.
// Also clears a name-gate-blocked attempt (its stashed file is this photo).
function removeStagedPhoto() {
  if (_genInFlight || _completedGenId) return; // ✕ is hidden in these states anyway
  _sourceCanvas = null;
  _sourceName = '';
  _croppedFile = null;
  _preupload = null; // the silently pre-uploaded copy is abandoned (harmless temp object)
  var adj = el('adjust-btn');
  if (adj) adj.disabled = true;
  var img = el('file-chip-img');
  if (img) img.removeAttribute('src');
  var fi = el('file-input');
  if (fi) fi.value = '';
  setFileHint('No Photo Selected');
  showNameError(false);
  clearError();
  updateStepFlow();
  track('photo_removed', { source_flow: sourceFlow() });
}

// ─── Inline progress ticker (clone of the wc77-loading pattern) ──────────────
// Same two-segment curve + park-at-99 + 150s recovery handoff, but writing to
// the inline Flow B elements instead of the modal (and stage copy per spec:
// Uploading → Preparing → Painting).

var FLOWB_TIMELINE = [
  [0,   'Uploading photo…'],
  [4,   'Preparing photo…'],
  [9,   'Painting your portrait…'],
  [70,  'Almost there — adding finishing touches…'],
  [150, 'Still painting — we haven’t lost it. Hang tight…'],
  [300, 'Still at it. Busy days can take a few minutes…'],
];
var TICK_DEADLINE_SEC = 150; // hand off to fetch-gen recovery (mirrors wc77-loading)

var _tickInterval = null;
var _tickStart = 0;
var _tickDeadlineFired = false;
var _tickGenId = null;

function tickerProgressPct(t) {
  if (t <= 50) return (t / 50) * 75;
  if (t <= 90) return 75 + ((t - 50) / 40) * 24;
  return 99;
}

function startTicker(generationId, elapsedOffsetSec) {
  stopTicker(false);
  _tickStart = performance.now() - (elapsedOffsetSec || 0) * 1000;
  _tickDeadlineFired = false;
  _tickGenId = generationId;
  var p = el('progress');
  if (p) p.hidden = false;
  // 2026-07-10 round-2 (Joe, item 11) — on standalone the progress bar lives
  // in its own orange card (Liquid renders it only there); show/hide it in
  // lockstep with the bar. el() returns null on every other render → no-op.
  var pc = el('progress-card');
  if (pc) pc.hidden = false;
  _tickInterval = setInterval(tickerTick, 1000);
  tickerTick();
}

function stopTicker(complete) {
  if (_tickInterval) { clearInterval(_tickInterval); _tickInterval = null; }
  var p = el('progress');
  if (!p) return;
  var pc = el('progress-card');
  if (complete) {
    var fill = el('progress-fill'), pct = el('progress-pct'), stage = el('progress-stage');
    if (fill) fill.style.width = '100%';
    if (pct) pct.textContent = '100%';
    if (stage) stage.textContent = 'Done — here’s your portrait!';
    setTimeout(function () { p.hidden = true; if (pc) pc.hidden = true; }, 1600);
  } else {
    p.hidden = true;
    if (pc) pc.hidden = true;
  }
}

function tickerTick() {
  var t = (performance.now() - _tickStart) / 1000;
  var entry = FLOWB_TIMELINE[0];
  for (var i = 0; i < FLOWB_TIMELINE.length; i++) {
    if (FLOWB_TIMELINE[i][0] <= t) entry = FLOWB_TIMELINE[i]; else break;
  }
  var stage = el('progress-stage'), fill = el('progress-fill'), pctEl = el('progress-pct');
  if (stage && stage.textContent !== entry[1]) stage.textContent = entry[1];
  var pct = tickerProgressPct(t);
  if (fill) fill.style.width = pct.toFixed(2) + '%';
  if (pctEl) pctEl.textContent = Math.round(pct) + '%';
  // Soft deadline → recovery poll owns the outcome (the long-held HTTP
  // response often dies while the gen finishes server-side — same reality
  // the modal flow learned on 2026-06-10).
  if (t >= TICK_DEADLINE_SEC && !_tickDeadlineFired) {
    _tickDeadlineFired = true;
    if (_genInFlight && !_recovering && _tickGenId) {
      recoverInline(_tickGenId, 'loader_deadline');
    }
  }
}

// ─── HEIC lazy decode + EXIF-safe canvas pipeline (spec §Flow B item 3) ──────

var _heicLoadPromise = null;

function isHeic(file) {
  var t = (file.type || '').toLowerCase();
  return t === 'image/heic' || t === 'image/heif' || /\.hei[cf]$/i.test(file.name || '');
}

// Vendored decoder (assets/wolden-heic2any.js) is ~1.3MB — loaded ONLY when a
// HEIC/HEIF file is actually picked, via a classic script tag (the vendored
// build is a UMD global, not an ES module).
// 2026-07-01 (Flow C) — decoder src no longer assumes Flow B's _root: this
// module's HEIC/decode helpers are now ALSO imported by wc77-flow-c.js
// (whose own root carries the same data-wc-heic-src attribute), so resolve
// from _root when Flow B booted, else from any flow root in the DOM.
function _heicDecoderSrc() {
  var host = _root || document.querySelector('[data-wc-heic-src]');
  return host ? host.getAttribute('data-wc-heic-src') : null;
}
function loadHeicDecoder() {
  if (window.heic2any) return Promise.resolve(window.heic2any);
  if (_heicLoadPromise) return _heicLoadPromise;
  _heicLoadPromise = new Promise(function (resolve, reject) {
    var src = _heicDecoderSrc();
    if (!src) { _heicLoadPromise = null; reject(new Error('heic decoder src missing')); return; }
    var s = document.createElement('script');
    s.src = src;
    s.onload = function () {
      if (window.heic2any) resolve(window.heic2any);
      else reject(new Error('heic decoder loaded but global missing'));
    };
    s.onerror = function () { _heicLoadPromise = null; reject(new Error('heic decoder failed to load')); };
    document.head.appendChild(s);
  });
  return _heicLoadPromise;
}

function heicToJpegBlob(file) {
  return loadHeicDecoder().then(function (heic2any) {
    return heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
  }).then(function (out) {
    // heic2any returns an array for multi-image containers — first frame wins.
    return Array.isArray(out) ? out[0] : out;
  });
}

// Decode ANY accepted blob to an EXIF-upright canvas capped at 2048px long
// edge (the spec's output ceiling — cropping later can only shrink it).
// EXIF path: createImageBitmap({imageOrientation:'from-image'}) where
// supported; the <img> fallback is also upright because CSS
// image-orientation:from-image has been the default in every evergreen
// browser since 2020 — drawImage(img) then bakes the upright pixels in.
function decodeToCanvas(blob) {
  var viaBitmap;
  if (typeof window.createImageBitmap === 'function') {
    viaBitmap = createImageBitmap(blob, { imageOrientation: 'from-image' })
      ['catch'](function () { return createImageBitmap(blob); });
  } else {
    viaBitmap = Promise.reject(new Error('createImageBitmap unsupported'));
  }
  return viaBitmap['catch'](function () {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(blob);
      var img = new Image();
      img.onload = function () { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = function () { URL.revokeObjectURL(url); reject(new Error('image decode failed')); };
      img.src = url;
    });
  }).then(function (src) {
    var w = src.width || src.naturalWidth;
    var h = src.height || src.naturalHeight;
    if (!w || !h) throw new Error('decoded image has no dimensions');
    var scale = Math.min(1, 2048 / Math.max(w, h));
    var canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    canvas.getContext('2d').drawImage(src, 0, 0, canvas.width, canvas.height);
    if (typeof src.close === 'function') { try { src.close(); } catch (e) { /* ignore */ } }
    return canvas;
  });
}

function prepareSource(file) {
  var toBlob = isHeic(file) ? heicToJpegBlob(file) : Promise.resolve(file);
  return toBlob.then(decodeToCanvas);
}

// ─── Crop dialog (modal state "adjust-photo") ─────────────────────────────────

function cropDefault() {
  var side = Math.round(0.8 * Math.min(_sourceCanvas.width, _sourceCanvas.height));
  _crop = {
    x: Math.round((_sourceCanvas.width - side) / 2),
    y: Math.round((_sourceCanvas.height - side) / 2),
    w: side,
    h: side,
  };
  _cropDefaultSide = side;
  _cropMoved = false;
}

function clampCrop() {
  var W = _sourceCanvas.width, H = _sourceCanvas.height;
  var MIN = Math.round(0.15 * Math.min(W, H)); // never smaller than 15% of short edge
  _crop.w = Math.max(MIN, Math.min(_crop.w, W));
  _crop.h = Math.max(MIN, Math.min(_crop.h, H));
  _crop.x = Math.max(0, Math.min(_crop.x, W - _crop.w));
  _crop.y = Math.max(0, Math.min(_crop.y, H - _crop.h));
}

function openCropDialog() {
  openModal('adjust-photo');
  // Layout settles after the section unhides — measure on the next frame.
  requestAnimationFrame(renderCropStage);
  track('crop_opened', {});
}

function renderCropStage() {
  var stage = modalEl('crop-stage');
  var canvas = modalEl('crop-canvas');
  if (!stage || !canvas || !_sourceCanvas) return;
  var host = stage.parentElement || stage;
  var maxW = Math.max(240, (host.clientWidth || 320) - 4);
  var maxH = Math.min(Math.round(window.innerHeight * 0.5), 440);
  _stageScale = Math.min(maxW / _sourceCanvas.width, maxH / _sourceCanvas.height);
  canvas.width = Math.max(1, Math.round(_sourceCanvas.width * _stageScale));
  canvas.height = Math.max(1, Math.round(_sourceCanvas.height * _stageScale));
  // The stage shrink-wraps the canvas so the crop box's absolute coordinates
  // ARE canvas coordinates (no offset math anywhere else).
  stage.style.width = canvas.width + 'px';
  stage.style.height = canvas.height + 'px';
  canvas.getContext('2d').drawImage(_sourceCanvas, 0, 0, canvas.width, canvas.height);
  positionCropBox();
}

function positionCropBox() {
  var box = modalEl('crop-box');
  if (!box || !_crop) return;
  box.style.left = Math.round(_crop.x * _stageScale) + 'px';
  box.style.top = Math.round(_crop.y * _stageScale) + 'px';
  box.style.width = Math.round(_crop.w * _stageScale) + 'px';
  box.style.height = Math.round(_crop.h * _stageScale) + 'px';
}

// Pointer-driven move/resize. One pair of document-level move/up listeners is
// installed per gesture and removed on pointerup — no persistent listeners.
function wireCropPointerHandlers() {
  var modal = getModal();
  if (!modal || modal.__wcFlowBCropWired) return;
  modal.__wcFlowBCropWired = true;

  var box = modal.querySelector('[data-wc-flow-b="crop-box"]');
  if (!box) return;

  box.addEventListener('pointerdown', function (e) {
    if (!_crop || !_sourceCanvas) return;
    e.preventDefault();
    var handle = e.target.closest('[data-wc-crop-handle]');
    var mode = handle ? handle.getAttribute('data-wc-crop-handle') : 'move';
    var startX = e.clientX, startY = e.clientY;
    var start = { x: _crop.x, y: _crop.y, w: _crop.w, h: _crop.h };
    var moved = false;

    function onMove(ev) {
      var dx = (ev.clientX - startX) / _stageScale;
      var dy = (ev.clientY - startY) / _stageScale;
      if (Math.abs(ev.clientX - startX) + Math.abs(ev.clientY - startY) > 2) moved = true;
      if (mode === 'move') {
        _crop.x = start.x + dx;
        _crop.y = start.y + dy;
      } else {
        // Corner resize anchored on the opposite corner (free aspect —
        // the generation input has no aspect requirement).
        if (mode.indexOf('e') !== -1) _crop.w = start.w + dx;
        if (mode.indexOf('s') !== -1) _crop.h = start.h + dy;
        if (mode.indexOf('w') !== -1) { _crop.x = start.x + dx; _crop.w = start.w - dx; }
        if (mode.indexOf('n') !== -1) { _crop.y = start.y + dy; _crop.h = start.h - dy; }
        if (_crop.w < 1) _crop.w = 1;
        if (_crop.h < 1) _crop.h = 1;
      }
      clampCrop();
      positionCropBox();
    }
    function onUp() {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      if (moved) _cropMoved = true;
    }
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  });
}

function zoomCrop(factor) {
  if (!_crop || !_sourceCanvas) return;
  // Zoom scales the crop box around its center: zoom-in = smaller box =
  // subject larger in the output. Identical output to scaling the image,
  // with none of the pan bookkeeping.
  var cx = _crop.x + _crop.w / 2, cy = _crop.y + _crop.h / 2;
  _crop.w = _crop.w * factor;
  _crop.h = _crop.h * factor;
  _crop.x = cx - _crop.w / 2;
  _crop.y = cy - _crop.h / 2;
  clampCrop();
  positionCropBox();
  _cropMoved = true;
}

function cropZoomFactor() {
  // >1 means zoomed IN relative to the default 80% box.
  var side = Math.sqrt(_crop.w * _crop.h) || _cropDefaultSide;
  return _cropDefaultSide ? +(_cropDefaultSide / side).toFixed(2) : 1;
}

function confirmCrop() {
  if (!_crop || !_sourceCanvas) return;
  var out = document.createElement('canvas');
  var x = Math.round(_crop.x), y = Math.round(_crop.y);
  var w = Math.max(1, Math.round(_crop.w)), h = Math.max(1, Math.round(_crop.h));
  out.width = w;
  out.height = h;
  out.getContext('2d').drawImage(_sourceCanvas, x, y, w, h, 0, 0, w, h);
  var zoom = cropZoomFactor();
  var moved = _cropMoved;
  out.toBlob(function (blob) {
    if (!blob) {
      showError('We couldn’t process that photo. Please try a different one.');
      closeModal();
      return;
    }
    var file = new File([blob], 'pet-photo.jpg', { type: 'image/jpeg' });
    track('crop_confirmed', { zoom: zoom, moved: moved });
    closeModal();
    setFileHint(_sourceName + ' — photo ready');
    // 2026-07-09 v2 (Joe) — STANDALONE no longer auto-generates on crop
    // DONE. The photo is stashed for the Start My Art button and silently
    // pre-uploaded in the background (no progress UI — the bar belongs to
    // the generation the customer explicitly starts). The step flow +
    // scroll walk them to the next step (Name, or the button). The TEST
    // arm keeps the original crop-DONE auto-start verbatim.
    if (_standalone) {
      _croppedFile = file;
      preuploadCropped(file);
      updateStepFlow();
      scrollToStepTarget();
      return;
    }
    emailGateThenGenerate(file);
  }, 'image/jpeg', 0.92); // spec: cropped JPEG, quality 0.92, ≤2048 long edge
}

// ─── Email capture gate (spec §Common 2 — BEFORE generation) ─────────────────
//
// 2026-07-02 APPLES-TO-APPLES STRIP (docs/flow-split-test/HANDOFF.md scope
// corrections): round-1 test arms must ask for exactly what the current
// modal flow asks for — which is NO email. All email behavior is therefore
// gated behind theme setting wc_flow_email_capture (default OFF):
//   OFF → the crop-DONE → generation path skips the gate entirely, zero
//         Klaviyo calls, and the orchestrator payload carries no email
//         extras (the base builder's emailCaptured:isEmailCaptured() +
//         shopifyCustomerId stand — identical to the modal flow, where the
//         server already knows a logged-in customer's email).
//   ON  → the original capture-BEFORE-generation behavior, unchanged.
// The Liquid side (wolden-flow-b/-c.liquid) gates the UI off the same
// setting, so OFF also renders no email markup at all. Code is KEPT, not
// deleted — the future email-capture test flips the setting.

export function emailCaptureEnabled() {
  return !!(window.WC_SETTINGS && window.WC_SETTINGS.wc_flow_email_capture === true);
}

/* 2026-07-01 (Flow C) — the fetch half of klaviyoSubscribe, extracted into a
   pure exported helper so Flow C reuses the SAME endpoint/behavior (silent
   skip on blank list, best-effort, never blocks generation) instead of
   forking it. Optional `props` become Klaviyo profile properties (declared
   via $fields, the legacy endpoint's custom-property mechanism) — Flow C's
   breed/birthday update rides through here. Flow B's behavior is unchanged:
   klaviyoSubscribe below still does its own consent read, then delegates. */
export function klaviyoListSubscribe(email, props) {
  try {
    // Master email gate: capture OFF (round-1 state) = zero Klaviyo calls,
    // full stop — this is the one choke point both flows' subscribe AND
    // Flow C's breed/birthday profile update ride through.
    if (!emailCaptureEnabled()) return;
    var listId = (window.WC_SETTINGS && window.WC_SETTINGS.wc_flow_klaviyo_list) || '';
    if (!listId || !email) return; // Joe hasn't picked the list yet — silent skip (spec)
    // Klaviyo's legacy list-subscribe endpoint: needs ONLY the list id (the
    // client JSON:API needs the account public key, which the theme doesn't
    // carry). Swap to the client API if/when a key setting exists.
    var body = 'g=' + encodeURIComponent(listId) + '&email=' + encodeURIComponent(email);
    if (props) {
      var names = [];
      for (var k in props) {
        if (Object.prototype.hasOwnProperty.call(props, k) && props[k]) {
          names.push(k);
          body += '&' + encodeURIComponent(k) + '=' + encodeURIComponent(props[k]);
        }
      }
      if (names.length) body += '&' + encodeURIComponent('$fields') + '=' + encodeURIComponent(names.join(','));
    }
    fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body,
    })['catch'](function () { /* best-effort — never blocks generation */ });
  } catch (e) { /* ignore */ }
}

function klaviyoSubscribe(email) {
  try {
    var consent = el('email-consent');
    if (consent && !consent.checked) return; // consent unchecked = no marketing subscribe
    klaviyoListSubscribe(email);
  } catch (e) { /* ignore */ }
}

function showEmailNote(msg) {
  var n = el('email-note');
  if (!n) return;
  n.textContent = msg || '';
  n.hidden = !msg;
}

function emailGateThenGenerate(file) {
  if (!emailCaptureEnabled()) {
    // Apples-to-apples (round-1): no email ask anywhere — generation starts
    // straight off crop DONE, exactly like the modal flow's upload does.
    startGeneration(file, null);
    return;
  }
  _croppedFile = file;
  var input = el('email-input');
  var typed = input ? String(input.value || '').trim() : '';
  if (validEmail(typed)) {
    // Dedup: a repeat generation with the same already-captured email is not
    // a new capture (spec: once per capture point, not once per generation).
    if (!isEmailCaptured() || getCapturedEmail() !== typed) onEmailCaptured(typed);
    startGeneration(file, typed);
    return;
  }
  var prior = isEmailCaptured() ? getCapturedEmail() : null;
  if (prior) {
    // Returning visitor — already captured once; don't re-gate (locked
    // decision is capture BEFORE generation, not capture EVERY generation).
    startGeneration(file, prior);
    return;
  }
  // Gate: hold the cropped file, walk them to the email block.
  var block = el('email-block');
  var cont = el('email-continue');
  if (cont) cont.hidden = false;
  showEmailNote('Enter your email to see your portrait — we’ll start painting the moment you do.');
  if (block) {
    block.classList.add('wolden-flow-b__email--attention');
    try { block.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { /* ignore */ }
  }
  if (input) { try { input.focus(); } catch (e) { /* ignore */ } }
}

function onEmailCaptured(email) {
  if (!emailCaptureEnabled()) return; // unreachable with the UI gated off — belt and braces
  captureEmail(email); // localStorage flag + value (shared with the modal flow)
  klaviyoSubscribe(email);
  track('email_captured', { source_flow: sourceFlow() });
}

function tryReleaseEmailGate() {
  if (!_croppedFile || _genInFlight) return;
  var input = el('email-input');
  var typed = input ? String(input.value || '').trim() : '';
  if (!validEmail(typed)) {
    showEmailNote('That email doesn’t look complete — double-check it and we’ll get painting.');
    return;
  }
  showEmailNote('');
  var block = el('email-block');
  if (block) block.classList.remove('wolden-flow-b__email--attention');
  var cont = el('email-continue');
  if (cont) cont.hidden = true;
  if (!isEmailCaptured() || getCapturedEmail() !== typed) onEmailCaptured(typed);
  startGeneration(_croppedFile, typed);
}

// ─── Generation pipeline (reused plumbing, inline UI) ─────────────────────────

function currentPetNameForPayload() {
  // 2026-07-09 dynamic-name — dynamic pages NEVER send petName (spec R3d):
  // the name rides the prompt appendix instead, and sending petName would
  // fire the Lambda's fixed-font data[3] composite on top of AI-painted
  // text — the name would print twice.
  if (_dynamicText) return null;
  if (!_nameEnabled || !_petNameVisible) return null;
  var input = el('name-input');
  var name = sanitizeName(input && input.value);
  return name || null;
}

function startGeneration(file, email) {
  if (_genInFlight) return;
  clearError();
  // 2026-07-09 dynamic-name — required-or-opt-out gate (spec R3a). A dynamic
  // page's source art carries a painted sample name, so every generation
  // MUST say what to do with it: repaint to a typed name, or remove it
  // ("No name" checked). Neither → block HERE — the one choke point every
  // generation path converges on (crop-DONE direct, email gate, gate
  // release) BEFORE any network call — so no presign/S3/orchestrator
  // request ever fires for a blocked attempt. Typing or checking the box
  // hides the error (wired in initFlowB). Non-dynamic pages skip this:
  // nameless generations are valid there (the overlay simply isn't drawn).
  if (_dynamicText) {
    var nonameBox = el('noname-checkbox');
    var typedName = sanitizeName((el('name-input') || {}).value);
    if (!typedName && !(nonameBox && nonameBox.checked)) {
      // 2026-07-09 v2: no stash/auto-resume — the error + the "Next" chip on
      // the Name card guide the customer, and they press Start My Art again.
      // (The cropped photo is NOT lost: _croppedFile holds it for the button.)
      showNameError(true);
      track('dynamic_name_blocked', {
        product_handle: productCtxFromRoot().productHandle,
        source_flow: sourceFlow(),
      });
      return;
    }
  }
  var productCtx = productCtxFromRoot();
  if (!productCtx.prompt || productCtx.prompt.length < 10) {
    showError('This product is missing its AI prompt. Please contact support.');
    return;
  }
  if (!productCtx.originalImageUrl) {
    showError('This product is missing its original image. Please contact support.');
    return;
  }

  _genInFlight = true;
  _recovering = false;
  _completedGenId = null;
  _croppedFile = null;
  var generationId = tempGenerationId();

  // Own pending marker (NOT wc_pending_gen — the shared reload recovery
  // would hydrate this gen into the reveal modal, which Flow B never opens).
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({
      generationId: generationId,
      startedAt: Date.now(),
      productHandle: productCtx.productHandle,
    }));
  } catch (e) { /* non-fatal */ }

  track('upload_started', {
    generation_id: generationId,
    product_id: productCtx.productId,
    product_handle: productCtx.productHandle,
    file_size_mb: +(file.size / (1024 * 1024)).toFixed(2),
    file_type: file.type || 'unknown',
    source_flow: sourceFlow(),
  });

  setButtonsBusy(true);
  startTicker(generationId, 0);
  // 2026-07-10 round-2 (Joe, item 10) — pressing Start used to just hide the
  // button, leaving the customer staring at nothing; walk them to the live
  // progress card so the payoff of the press is visible. Standalone only —
  // the test arm's inline bar sits right where the customer already is.
  if (_standalone) {
    var progressTarget = el('progress-card') || el('progress');
    if (progressTarget) {
      try { progressTarget.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { /* ignore */ }
    }
  }
  var startedAt = Date.now();

  // 2026-07-09 v2 — reuse the silent background pre-upload when it's for
  // THIS exact file (standalone crop-DONE started it). A null resolution
  // means the pre-upload failed: fall back to uploading now, exactly as if
  // no pre-upload existed. Any other path keeps the original inline upload.
  (function () {
    if (_standalone && _preupload && _preupload.file === file) {
      return _preupload.promise.then(function (resp) {
        if (resp) return resp;
        return requestPresignedUrl(generationId, file).then(function (r) {
          return uploadToS3(r.uploadUrl, file).then(function () { return r; });
        });
      });
    }
    return requestPresignedUrl(generationId, file).then(function (presignerResp) {
      return uploadToS3(presignerResp.uploadUrl, file).then(function () { return presignerResp; });
    });
  })()
    .then(function (presignerResp) {
      var elapsed = ((Date.now() - startedAt) / 1000).toFixed(1);
      _lastPublicUrl = presignerResp.publicUrl;
      track('upload_succeeded', {
        generation_id: generationId,
        product_handle: productCtx.productHandle,
        elapsed_sec: +elapsed,
        public_url: presignerResp.publicUrl,
      });

      var petName = currentPetNameForPayload();
      _petNameSentAtGeneration = petName || '';
      var emailOn = emailCaptureEnabled();
      // Base payload is the shared builder (single source of truth with the
      // modal flow); Flow B's additive fields ride in extras — the Lambda
      // contract additions (petName → NBP data[3] named 2K) plus, ONLY when
      // email capture is enabled, the email-before-generation fields.
      // Capture OFF (round-1 apples-to-apples state) → undefined keys are
      // dropped by the builder, so the payload's email story is byte-
      // identical to the modal flow's (emailCaptured:isEmailCaptured() +
      // shopifyCustomerId; the platform supplies a logged-in customer's
      // email server-side — we never do).
      // 2026-07-09 dynamic-name (spec R3d) — dynamic pages replace the
      // petName/data[3] composite with a prompt appendix: dynName '' means
      // the "No name" opt-out is checked → the removal appendix ('' || null
      // → null); otherwise the prompt-safe sanitized name → the repaint
      // appendix. ar45:true asks the image lambda for the text-art 2:3→4:5
      // conversion. The builder merges extras LAST with no whitelist, so
      // the extras `prompt` key overrides productCtx.prompt with no builder
      // change; on non-dynamic renders both keys are undefined → dropped by
      // the builder → payloads stay byte-identical to before.
      var dynName = null;
      if (_dynamicText) {
        var nonameOn = !!(el('noname-checkbox') && el('noname-checkbox').checked);
        dynName = nonameOn ? '' : promptNameForAppendix((el('name-input') || {}).value);
      }
      // 2026-07-10 (Joe, version B) — the optional dates line, formatted by
      // OUR code (never free-typed), painted beneath the name.
      var dynDates = (_dynamicDates && dynName) ? paintedDatesString() : '';
      var payload = buildOrchestratorPayload(productCtx, generationId, presignerResp.publicUrl, {
        email: emailOn ? (email || getCapturedEmail() || '') : undefined,
        emailCaptured: emailOn ? true : undefined,
        // petName is null in dynamic mode (currentPetNameForPayload) — the
        // name rides `prompt` below instead; undefined = key dropped.
        petName: petName || undefined,
        prompt: _dynamicText ? productCtx.prompt + buildDynamicPromptAppendix(dynName || null, dynDates || null) : undefined,
        ar45: _dynamicText ? true : undefined,
        // Standalone page sells Canvas + Framed only — skip the paint-kit
        // shell + its 9 variants server-side (orchestrator contract
        // 2026-07-01; undefined keys are dropped by the payload builder,
        // so test-arm payloads are byte-identical to before).
        skipProducts: _standalone ? ['pbn'] : undefined,
        // 2026-07-02 — arm-of-record (tracking-layer-spec §1.3 layer 4):
        // standalone generations are OUTSIDE the test, so override the
        // assigner-derived arm the shared builder stamped with the
        // standalone identity (the same value sourceFlow() stamps on this
        // page's analytics events). Test-arm renders leave the builder's
        // WC_FLOW-derived flowArm untouched (undefined keys are dropped).
        flowArm: _standalone ? 'watercolor_standalone' : undefined,
      });

      track('generation_started', {
        generation_id: generationId,
        product_handle: productCtx.productHandle,
        // Capture ON → the gate guaranteed an email; OFF → report the
        // platform truth (same semantics as the modal flow's tracker).
        email_captured: emailOn ? true : isEmailCaptured(),
        has_pet_name: !!petName,
        source_flow: sourceFlow(),
      });

      runFullGeneration(payload,
        function onPhase1(orchResult) {
          track('generation_phase1_succeeded', {
            generation_id: orchResult.generationId,
            bubble_id: orchResult.bubbleId,
            phase1_elapsed_sec: +(((Date.now() - startedAt) / 1000).toFixed(1)),
            product_handle: productCtx.productHandle,
            source_flow: sourceFlow(),
          });
          handleOrchResult(orchResult, 'orchestrator');
        },
        function onPhase2(phase2) {
          track('generation_phase2_succeeded', {
            generation_id: phase2.generationId,
            phase2_elapsed_sec: +(((Date.now() - startedAt) / 1000).toFixed(1)),
          });
          handlePhase2Result(phase2);
        },
        function onError(err, phase) {
          if (phase === 'phase2' || phase === 'phase1.5') {
            // Non-fatal — the customer already has art + variant maps.
            log('[flow-b] generation ' + phase + ' error (non-fatal)', err && err.message);
            track('generation_' + String(phase).replace('.', '_') + '_failed', { error: err && err.message });
            return;
          }
          if (err && err.quotaGate && err.body) {
            // Flow B shows quota gates inline (no popup wiring in this flow;
            // mirrors the live store's behavior for DAILY_LIMIT).
            _genInFlight = false;
            stopTicker(false);
            setButtonsBusy(false);
            try { sessionStorage.removeItem(PENDING_KEY); } catch (e2) { /* ignore */ }
            track('generation_quota_gated', { generation_id: generationId, gate: err.body.error });
            showError((err.body && err.body.message) || 'You’ve hit today’s limit. Try again tomorrow!');
            return;
          }
          if (_recovering) return; // deadline poll already owns the outcome
          recoverInline(generationId, 'orchestrator_error', err);
        },
        function onPhase1_5(p15) {
          track('generation_phase1_5_succeeded', { generation_id: p15.generationId });
        }
      );
    })
    ['catch'](function (err) {
      _genInFlight = false;
      stopTicker(false);
      setButtonsBusy(false);
      try { sessionStorage.removeItem(PENDING_KEY); } catch (e) { /* ignore */ }
      var displayMsg = (err && err.wcUserFacing && err.message) ? err.message : 'Upload failed. Please try again.';
      showError(displayMsg);
      track('upload_failed', {
        generation_id: generationId,
        message: (err && err.message) || 'unknown',
        wc_user_facing: !!(err && err.wcUserFacing),
        source_flow: sourceFlow(),
      });
    });
}

// Dropped-connection / deadline recovery — the SAME fetch-gen poll the modal
// flow uses (wc77-upload.pollFetchGen), rendered inline instead of modally.
function recoverInline(generationId, trigger, originalErr) {
  if (_recovering) return;
  _recovering = true;
  track('generation_recovery_started', {
    generation_id: generationId,
    trigger: trigger,
    error: (originalErr && originalErr.message) || trigger,
  });
  pollFetchGen(generationId, 30, 6000, function () { return _completedGenId === generationId; })
    .then(function (out) {
      _recovering = false;
      if (out.stopped) return; // orchestrator response landed mid-poll
      if (out.body) {
        track('generation_recovered_after_drop', {
          generation_id: generationId,
          original_error: (originalErr && originalErr.message) || trigger,
        });
        var orchResult = mapFetchGenToOrchResult(out.body, generationId);
        handleOrchResult(orchResult, 'recovery');
        return;
      }
      failGeneration(generationId, originalErr, trigger);
    });
}

function failGeneration(generationId, err, phase) {
  if (_completedGenId === generationId) return; // a later success already won
  _genInFlight = false;
  stopTicker(false);
  setButtonsBusy(false);
  try { sessionStorage.removeItem(PENDING_KEY); } catch (e) { /* ignore */ }
  track('generation_failed', {
    generation_id: generationId,
    phase: (err && err.phase) || phase || 'unknown',
    message: (err && err.message) || 'recovery_exhausted',
    source_flow: sourceFlow(),
  });
  showError('We couldn’t finish your portrait this time. Your photo is saved — please try again in a minute.');
}

function handleOrchResult(orchResult, source) {
  if (!orchResult || !orchResult.generationId) return;
  if (_completedGenId === orchResult.generationId) return; // double-hydrate guard
  _completedGenId = orchResult.generationId;
  _genInFlight = false;
  _recovering = false;
  stopTicker(true);
  setButtonsBusy(false);
  try { sessionStorage.removeItem(PENDING_KEY); } catch (e) { /* ignore */ }

  var R = hydrateR(orchResult);
  persistGen(R);
  renderPreview(R);

  // 2026-07-02 QA round-4 — champion-parity straight-on poll (see the helper's
  // comment). Recovery-path results may already carry the URL (hydrateR).
  if (orchResult.straightOnPending && !R.straightOnMockupUrl) {
    startStraightOnPoll(R.generationId, applyStraightOn, 'flow-b');
  }

  // Native-form ATC wiring: child variant id + the properties[_*] inputs.
  try { updatePdpVariantState(); } catch (e) { log('[flow-b] updatePdpVariantState failed', e && e.message); }
  try { applyPdpVisibility(); } catch (e) { /* non-fatal */ }
  writePetNameProperty();

  // 2026-07-10 round-2 (Joe, item 12) — art just landed on the standalone
  // page: pre-select Frame at the default 8x10 (the configuration most
  // customers buy). LIVE-completion path only on purpose — a reload
  // re-hydration (hydrateFromStore) must respect whatever the customer
  // picked before reloading.
  // Round-3 (Joe, 2026-07-11): scroll to the TOP, not the preview box —
  // the auto-frame just jumped the gallery deck to the framed 8x10 mockup,
  // and THAT is the money shot the customer should land on.
  if (_standalone) {
    try { autoSelectFramedDefault(); } catch (e) { log('[flow-b] auto-frame failed', e && e.message); }
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (e) { try { window.scrollTo(0, 0); } catch (e2) { /* ignore */ } }
  }

  // result_shown is the funnel's canonical generation_complete UX moment
  // (connector keys on it) — Flow B's reveal IS the inline preview.
  // surface stays 'flow_b_inline' in BOTH modes — the funnel connector keys
  // step 4 on this event; standalone/source_flow are additive separators.
  track('result_shown', {
    generation_id: R.generationId,
    surface: 'flow_b_inline',
    has_pet_name: !!_petNameSentAtGeneration,
    result_source: source,
    standalone: _standalone,
    source_flow: sourceFlow(),
  });
  track('preview_rendered', { generation_id: R.generationId });

  document.dispatchEvent(new CustomEvent('wolden:generation-complete', {
    bubbles: true,
    detail: { generationId: R.generationId, data: orchResult.data, product: R.productCtx },
  }));
}

// 2026-07-02 QA round-4 (root cause from round-3) — Phase 2 arrival was
// track()-only: the payload (real paint-kit composite, PBN B&W breakdown,
// paint-kit Shopify variants) was discarded, so the reveal kept the generic
// placeholder tiles forever and the persisted snapshot never learned the
// phase-2 URLs. Mirror the champion path (wc77-upload.js onPhase2):
//   1. updateResultPhase2(phase2) — hydrates R (WC._currentResult is SHARED
//      with the modal machinery), repaints the modal decks idempotently
//      (hidden on this arm — harmless), refreshes pricing, replays queued
//      ATC clicks, and updates the in-memory stashes.
//   2. Repaint the ON-PAGE PDP gallery (Flow B's reveal is inline, not the
//      modal) so the visible slot model picks up the new URLs.
//   3. Merge the phase-2 fields into the SAVED gen snapshot: the gen-store
//      add() is insert-only (duplicate id → snapshot untouched), so without
//      updateMockups a reload would rehydrate the placeholder tiles.
function handlePhase2Result(phase2) {
  try {
    updateResultPhase2(phase2);
  } catch (e) { log('[flow-b] updateResultPhase2 failed', e && e.message); }
  try {
    var R = ensureR();
    // Defensive: a phase-2 body that carries the straight-on front shot
    // (fetch-gen response shape) hydrates the canvas deck's 3rd slot —
    // same mapping the champion's showResult applies (wc77-result.js).
    if (phase2 && phase2.straightOn && phase2.straightOn.url) {
      R.straightOnMockupUrl = phase2.straightOn.url;
    }
    renderUnifiedGallery();
  } catch (e) { log('[flow-b] phase2 gallery repaint failed', e && e.message); }
  try {
    var R2 = ensureR();
    updateMockups(R2.generationId, {
      paintKit:   R2.paintKitMockup,
      pbnBw:      R2.pbnBwUrl,
      pbnColored: R2.pbnColoredUrl,
    });
  } catch (e) { log('[flow-b] phase2 persist failed', e && e.message); }
}

// 2026-07-10 round-2 (Joe, item 12) — pre-select the Frame option the moment
// art lands. A programmatic .click() on the REAL Version-D radio runs the
// exact same delegated handler chain a human tap does (state, Black default
// seed, mockup deck jump, variant id, clean-break) — never a divergent copy.
// The customer can still tap No Frame; nothing is locked.
function autoSelectFramedDefault() {
  var R = ensureR();
  if (R.activeFormat === 'canvas_framed') return; // already framed
  var frame = _root.querySelector('input[data-wc-action="select-canvas-frame-mode-d"][data-wc-frame-mode="frame"]');
  if (frame && !frame.checked) { frame.click(); return; }
  // Fallback surface: the "Add a frame" upsell checkbox drives the same mode.
  var upsell = _root.querySelector('input[data-wc-action="select-canvas-frame-upsell-d"]');
  if (upsell && !upsell.checked) upsell.click();
}

function setButtonsBusy(busy) {
  var up = el('upload-btn'), adj = el('adjust-btn');
  if (up) up.disabled = !!busy;
  if (adj) adj.disabled = !!busy || !_sourceCanvas;
  // 2026-07-09 UI pass — every generation state transition (start, fail,
  // quota-gate, success, resume) already funnels through here, so this one
  // call keeps the standalone step attribute in lockstep. No-op off
  // standalone.
  updateStepFlow();
}

// ─── Preview + name overlay ───────────────────────────────────────────────────

function renderPreview(R) {
  var img = el('preview-img');
  var ph = el('preview-placeholder');
  if (!img || !R.artDisplayUrl) return;
  img.hidden = false;
  if (ph) ph.hidden = true;
  img.onload = function () { redrawNameOverlay(); };
  img.src = R.artDisplayUrl;
  var up = el('upload-btn');
  if (up) up.textContent = 'Change Photo';
  var adj = el('adjust-btn');
  if (adj) adj.disabled = !_sourceCanvas; // source survives in-session only
  // 2026-07-02 QA round-3 (round-1 #13) — a post-reload hydration (or a
  // recovery-poll completion) renders the art preview while the Liquid's
  // static "No Photo Selected" hint survived, telling the customer they had
  // no photo with their portrait sitting right below it. The in-session file
  // name only lives in memory, so when it's gone say the truthful thing:
  // the photo IS saved with this portrait.
  if (!_sourceName) setFileHint('Photo saved with your portrait');
  // 2026-07-09 dynamic-name (spec R3f) — once art exists, the name is BAKED
  // INTO the pixels; post-gen edits can't change it (unlike the overlay
  // flow, where edits repaint the client preview live). Lock the input +
  // opt-out checkbox so the UI can't promise an edit it can't deliver.
  // Placed HERE (not handleOrchResult) so reload hydration locks too —
  // renderPreview is the single "art is on screen" moment for both paths.
  if (_dynamicText) {
    var nameField = el('name-input');
    if (nameField) nameField.disabled = true;
    var nonameBox = el('noname-checkbox');
    if (nonameBox) nonameBox.disabled = true;
    // 2026-07-10 (version B) — dates are baked into the art too; lock them.
    ['date-from', 'date-to', 'dates-monthyear'].forEach(function (k) {
      var f = el(k);
      if (f) f.disabled = true;
    });
  }
}

function redrawNameOverlay() {
  // 2026-07-09 dynamic-name — no client overlay on dynamic pages: the AI
  // bakes the name into the art pixels themselves, so drawing the fixed-font
  // overlay on top would show the name twice. Early-return (rather than
  // touching call sites) keeps every existing caller harmless; the canvas
  // stays at its Liquid-rendered `hidden` state.
  if (_dynamicText) return;
  if (!_nameEnabled) return;
  var img = el('preview-img');
  var canvas = el('name-canvas');
  if (!img || img.hidden || !canvas) return;
  var name = _petNameVisible ? sanitizeName((el('name-input') || {}).value) : '';
  var w = img.clientWidth, h = img.clientHeight;
  if (!w || !h) { canvas.hidden = true; return; }
  var dpr = window.devicePixelRatio || 1;
  canvas.width = Math.round(w * dpr);
  canvas.height = Math.round(h * dpr);
  canvas.style.width = w + 'px';
  canvas.style.height = h + 'px';
  var ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  canvas.hidden = !name;
  if (!name) return;

  // ── Lambda geometry contract (see module header) — proportional, so it
  // renders identically at preview size and at 2K print size. The 1px
  // minimum offset is per-raster-space by design (the Lambda applies it in
  // 2K space; we apply it in canvas space) — both round the same visual.
  var W = canvas.width, H = canvas.height;
  var fontSize = W * 0.10 * Math.min(1, 12 / name.length);
  var draw = function () {
    ctx.font = fontSize + 'px "WC Great Vibes"';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    var x = W / 2, y = H * 0.85;
    var offset = Math.max(1, fontSize * 0.04);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
    ctx.fillText(name, x, y + offset);
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(name, x, y);
  };
  try {
    if (document.fonts && document.fonts.load) {
      document.fonts.load(Math.max(10, Math.round(fontSize)) + 'px "WC Great Vibes"').then(draw, draw);
    } else {
      draw();
    }
  } catch (e) { draw(); }
}

function writePetNameProperty() {
  if (!_nameEnabled) return;
  var R = window.WC && window.WC._currentResult;
  if (!R || !R.generationId) return; // properties only matter once ATC is live
  // 2026-07-09 dynamic-name — dynamic pages key "no name" off the opt-out
  // checkbox (the Show/Hide toggle is hidden there, so _petNameVisible is
  // meaningless): checked → record an empty _pet_name even though the input
  // keeps its typed value (kept only so un-checking restores it, never for
  // the order). Non-dynamic branch unchanged.
  var name;
  if (_dynamicText) {
    var nonameBox = el('noname-checkbox');
    name = (nonameBox && nonameBox.checked) ? '' : sanitizeName((el('name-input') || {}).value);
    // 2026-07-10 (version B) — record the painted dates on the order too.
    if (_dynamicDates) {
      try { writeFormProperty('_pet_dates', paintedDatesString()); } catch (e) { /* non-fatal */ }
    }
  } else {
    name = _petNameVisible ? sanitizeName((el('name-input') || {}).value) : '';
  }
  try { writeFormProperty('_pet_name', name); } catch (e) { log('[flow-b] _pet_name write failed', e && e.message); }
}

// ─── Reload resume + store hydration ─────────────────────────────────────────

function resumePendingGen() {
  var raw;
  try { raw = sessionStorage.getItem(PENDING_KEY); } catch (e) { return; }
  if (!raw) return;
  var pending;
  try { pending = JSON.parse(raw); } catch (e) { pending = null; }
  if (!pending || !pending.generationId) {
    try { sessionStorage.removeItem(PENDING_KEY); } catch (e) { /* ignore */ }
    return;
  }
  var ageMs = Date.now() - (pending.startedAt || 0);
  if (ageMs > 15 * 60 * 1000) {
    try { sessionStorage.removeItem(PENDING_KEY); } catch (e) { /* ignore */ }
    return;
  }
  log('[flow-b] pending gen found — resuming inline', pending.generationId);
  _genInFlight = true;
  setButtonsBusy(true);
  startTicker(pending.generationId, Math.min(ageMs / 1000, TICK_DEADLINE_SEC - 1));
  // Round-3 (Joe, 2026-07-11) — a phone-lock reload lands at the TOP of the
  // page while the still-ticking progress card sits below the fold, which
  // reads as "my art is lost." Walk the customer back to it. Standalone
  // only; delayed a beat so the browser's own scroll restoration loses.
  if (_standalone) {
    setTimeout(function () {
      var pc = el('progress-card') || el('progress');
      if (pc && _genInFlight) {
        try { pc.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { /* ignore */ }
      }
    }, 600);
  }
  pollFetchGen(pending.generationId, 5, 4000, function () { return _completedGenId === pending.generationId; })
    .then(function (out) {
      if (out.stopped) return;
      if (out.body) {
        handleOrchResult(mapFetchGenToOrchResult(out.body, pending.generationId), 'reload_recovery');
        return;
      }
      // Not ready yet — keep the marker (it self-expires); hand the wait to
      // the ticker's 150s deadline recovery like a live generation.
      _tickGenId = pending.generationId;
    });
}

function hydrateFromStore() {
  try {
    var p = productLite();
    var key = getNamespaceKey(p);
    if (!key) return;
    var data = read(key);
    if (!data || !data.gens || !data.gens.length) return;
    var active = null;
    for (var i = 0; i < data.gens.length; i++) {
      if (data.gens[i].id === data.activeGenId) { active = data.gens[i]; break; }
    }
    if (!active) active = data.gens[data.gens.length - 1];
    if (!active || !active.imageUrl) return;

    var R = ensureR();
    // Don't clobber a fresher in-memory result (e.g. wc2-hydration already ran).
    if (!R.generationId) {
      R.generationId = active.id;
      R.bubbleId = active.bubbleId || null;
      R.artDisplayUrl = active.imageUrl;
      R.petImageUrl = active.petPhotoUrl || R.petImageUrl || null;
      var snap = active.snapshot || {};
      R.canvasMockups = snap.canvasMockups || null;
      R.paintKitMockup = snap.paintKitMockup || null;
      R.pbnBwUrl = snap.pbnBwUrl || null;
      R.pbnColoredUrl = snap.pbnColoredUrl || null;
      R.canvasShopify = snap.canvasShopify || null;
      R.canvasFramedShopify = snap.canvasFramedShopify || null;
      R.paintKitShopify = snap.paintKitShopify || null;
      // 2026-07-02 QA round-3 (round-1 #10) -- rehydrate the framed /
      // straight-on mockups so the reveal decks match a fresh generation.
      R.canvasFramedMockups = snap.canvasFramedMockups || null;
      R.straightOnMockupUrl = snap.straightOnMockupUrl || null;
      R.productCtx = productCtxFromRoot();
    }
    seedPickerState();
    _completedGenId = R.generationId;
    renderPreview(R);
    try { updatePdpVariantState(); } catch (e) { /* non-fatal */ }
    try { applyPdpVisibility(); } catch (e) { /* non-fatal */ }
    updateStepFlow(); // 2026-07-09 — reload hydration lands directly in 'done'
    writePetNameProperty();
    log('[flow-b] hydrated prior gen from store', R.generationId);
  } catch (e) {
    log('[flow-b] hydrateFromStore failed', e && e.message);
  }
}

// ─── ATC tracking (native-form path) ──────────────────────────────────────────
// The modal flow fires wolden_add_to_cart from its own button; the native
// path had no emit. Bubble-phase listener so the capture-phase ATC guard has
// already decided — if it blocked (pre-gen), defaultPrevented is true and we
// skip (the guard fired atc_blocked_no_generation itself).

var _atcTrackingWired = false;

function wireAtcTracking() {
  if (_atcTrackingWired) return; // document-level — must not stack on re-init
  _atcTrackingWired = true;
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented) return;
    var trigger = e.target.closest(
      'add-to-cart-component button, buy-buttons-component button, .product-form__submit, [data-add-to-cart], button[name="add"]'
    );
    if (!trigger) return;
    if (trigger.closest('[data-wc-component="modal"]')) return;
    var R = window.WC && window.WC._currentResult;
    if (!R || !R.generationId) return;
    var pricing = null;
    try { pricing = getCurrentPricing(); } catch (err) { /* non-fatal */ }
    track('add_to_cart', {
      generation_id: R.generationId,
      atc_path: 'native_form',
      format: R.activeFormat,
      value: pricing && pricing.sale,
      has_pet_name: !!(_nameEnabled && _petNameVisible && sanitizeName((el('name-input') || {}).value)),
      source_flow: sourceFlow(),
    });
  });
}

// ─── Init ─────────────────────────────────────────────────────────────────────

export function initFlowB() {
  var root = document.querySelector('[data-wc-component="flow-b"]');
  if (!root || root.__wcFlowBInit) return;
  root.__wcFlowBInit = true;
  _root = root;
  // Theme-editor section reloads replace the section DOM and re-run boot():
  // the guard above passes for the FRESH root, so the element cache must be
  // dropped or every el() lookup would return detached nodes.
  _els = {};

  // Standalone watercolor page (flow-ui-spec §Standalone): the snippet
  // rendered with standalone:true stamps this attribute. Everything below
  // that branches on _standalone is additive — a root WITHOUT the attribute
  // behaves exactly as before.
  _standalone = _root.hasAttribute('data-wc-standalone');
  // 2026-07-09 dynamic-name (spec R3) — dynamic-text page: standalone AND
  // the Liquid stamped data-wc-art-text-dynamic from the product's
  // custom.art_text_dynamic metafield. The _standalone guard is belt and
  // braces (the Liquid only emits the attribute on standalone renders);
  // every dynamic behavior in this module gates on this flag, so a root
  // without the attribute behaves byte-identically to before.
  _dynamicText = _standalone && _root.getAttribute('data-wc-art-text-dynamic') === 'true';
  // 2026-07-10 (Joe) — VERSION B switch (name required + optional dates).
  _dynamicDates = _dynamicText && _root.getAttribute('data-wc-art-text-dates') === 'true';

  // Name gate (2026-07-02 apples-to-apples strip): the TEST arm renders no
  // name UI at all — the Liquid gates the name block to standalone renders
  // only, so el('name-block') is null here for test-arm visitors. The
  // sub === 'name_on' check is DORMANT plumbing (pickSub in the assigner
  // returns null for every arm today) kept for a future sub-variant test;
  // the defensive remove() keeps a stray block out of the focus order /
  // a11y tree if markup and assignment ever disagree.
  var sub = (window.WC_FLOW && window.WC_FLOW.subVariant) || null;
  _nameEnabled = _standalone || sub === 'name_on';
  var nameBlock = el('name-block');
  if (!_nameEnabled && nameBlock) nameBlock.remove();

  seedPickerState();
  // 2026-07-01 verification fix — repaint the picker DOM from the R just
  // seeded above. WHY: on test products THREE copies of the pdp-options
  // snippet co-reside at parse time (current arm / flow-b / flow-c) and share
  // radio group names (name="wc-pdp-*"), so the HTML parser leaves only the
  // LAST copy's (flow-c's) radios checked — unchecking flow-b's. wc77-init's
  // subtree removal then deletes that last copy for watercolor visitors,
  // leaving Flow B's ALWAYS-VISIBLE pre-generation pickers with no format
  // tile or size pill selected until the first click. The shared refresh
  // helpers re-check the radios from R (the same chain every other surface
  // heals through); a no-op when the parsed state already matches.
  try {
    refreshActiveBranches();
    refreshSizeFramePills();
  } catch (e) { log('[flow-b] initial picker repaint failed', e && e.message); }
  // 2026-07-09 UI pass (Joe): the standalone per-size pill prices were
  // REMOVED (pills now match every other customizable PDP — bare native
  // pills, live price on the PDP price row). In their place, standalone
  // boots the guided step flow: ✕-remove on the staged-photo chip, and the
  // step attribute that moves the orange marker down the column.
  if (_standalone) {
    var chipRemove = el('file-chip-remove');
    if (chipRemove) chipRemove.addEventListener('click', removeStagedPhoto);
    updateStepFlow();
  }
  wireCropPointerHandlers();
  wireAtcTracking();

  // Upload / change photo
  var uploadBtn = el('upload-btn');
  var fileInput = el('file-input');
  if (uploadBtn && fileInput) {
    uploadBtn.addEventListener('click', function () { fileInput.click(); });
    fileInput.addEventListener('change', function (e) {
      var file = e.target.files && e.target.files[0];
      // Reset so re-picking the same file re-fires change.
      e.target.value = '';
      if (file) onFilePicked(file);
    });
    // 2026-07-12 (Joe) — FIRST-TAP bridge handoff. An inline script in the
    // snippet makes Upload Photo open the native picker BEFORE this module
    // arrives over a slow connection (Joe: 4-5 dead taps). This flag tells
    // the bridge to stand down (its listener would otherwise double-open
    // the picker), and a photo picked while the module was still loading is
    // processed now — the input's files[] held it for us (the bridge
    // attaches no change listener on purpose; one owner).
    window.__wcFlowBReady = true;
    var earlyFile = fileInput.files && fileInput.files[0];
    if (earlyFile && !_sourceCanvas) {
      fileInput.value = ''; // the File reference stays valid after the reset
      onFilePicked(earlyFile);
    }
  }

  // Adjust photo → reopen the crop dialog over the in-session source.
  var adjustBtn = el('adjust-btn');
  if (adjustBtn) {
    adjustBtn.addEventListener('click', function () {
      if (!_sourceCanvas || _genInFlight) return;
      openCropDialog();
    });
  }

  // Crop dialog buttons — delegated on the modal (shared shell).
  var modal = getModal();
  if (modal && !modal.__wcFlowBActionsWired) {
    modal.__wcFlowBActionsWired = true;
    modal.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-wc-action]');
      if (!trigger) return;
      var action = trigger.getAttribute('data-wc-action');
      if (action === 'flow-b-crop-done') { e.preventDefault(); confirmCrop(); }
      else if (action === 'flow-b-crop-cancel') {
        e.preventDefault();
        closeModal();
        if (!_completedGenId) setFileHint(_sourceName ? _sourceName + ' — press Adjust Photo to continue' : 'No Photo Selected');
      }
      else if (action === 'flow-b-zoom-in')  { e.preventDefault(); zoomCrop(1 / 1.15); }
      else if (action === 'flow-b-zoom-out') { e.preventDefault(); zoomCrop(1.15); }
    });
  }
  // Re-fit the crop stage on viewport changes while the dialog is open.
  // Window-level — guarded so section-reload re-inits don't stack copies.
  if (!window.__wcFlowBResizeWired) {
    window.__wcFlowBResizeWired = true;
    window.addEventListener('resize', function () {
      var m = getModal();
      if (m && !m.hidden && m.getAttribute('data-wc-modal-state') === 'adjust-photo') renderCropStage();
      redrawNameOverlay();
    });
  }

  // Email gate release: dedicated continue button + Enter in the input.
  var emailInput = el('email-input');
  var emailContinue = el('email-continue');
  if (emailContinue) emailContinue.addEventListener('click', tryReleaseEmailGate);
  if (emailInput) {
    emailInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); tryReleaseEmailGate(); }
    });
  }

  // Name overlay: live redraw as they type + Show/Hide toggle.
  if (_nameEnabled) {
    var nameInput = el('name-input');
    if (nameInput) {
      nameInput.addEventListener('input', function () {
        // 2026-07-09 dynamic-name — typing satisfies the required-or-opt-out
        // gate, so clear its inline error the moment they type (no-op on
        // non-dynamic pages: the error element doesn't render there).
        if (_dynamicText) showNameError(false);
        redrawNameOverlay();
        writePetNameProperty();
        // 2026-07-09 v2 — a valid name moves the "Next" chip + orange marker
        // from the Name card onto the Start My Art button (and back, if the
        // name is deleted again). Never starts anything by itself.
        updateStepFlow();
      });
    }
    var toggle = el('name-toggle');
    if (toggle) {
      toggle.addEventListener('click', function () {
        _petNameVisible = !_petNameVisible;
        toggle.textContent = _petNameVisible ? 'Hide' : 'Show';
        toggle.setAttribute('aria-pressed', _petNameVisible ? 'true' : 'false');
        track('name_toggled', { visible: _petNameVisible });
        redrawNameOverlay();
        writePetNameProperty();
      });
    }
    // 2026-07-09 dynamic-name (spec R3a) — "No name" opt-out wiring. The
    // checkbox only renders on dynamic pages (Liquid-gated), so this is
    // naturally inert everywhere else. One-way sync: checked → input
    // disabled with its typed value KEPT (un-checking restores it;
    // clearing would punish an accidental tick); unchecked → re-enabled.
    // Both paths hide the gate's inline error, refresh the overlay (a
    // harmless no-op in dynamic mode — belt and braces) and re-record the
    // _pet_name cart property (checked writes '').
    var nonameBox = el('noname-checkbox');
    if (nonameBox) {
      nonameBox.addEventListener('change', function () {
        var nameField = el('name-input');
        if (nameField) nameField.disabled = nonameBox.checked;
        showNameError(false);
        redrawNameOverlay();
        writePetNameProperty();
        // 2026-07-09 v2 (Joe) — the opt-out NEVER auto-starts the art (the
        // earlier auto-resume here confused a real test: checking the box
        // kicked off a generation). It only advances the step marker; the
        // customer presses Start My Art themselves.
        updateStepFlow();
      });
    }
    // 2026-07-09 v2 — Enter in the name field must not submit/act; the
    // button is the only trigger. (The 'input' handler above already moved
    // the step marker as they typed.)
    if (nameInput) {
      nameInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') e.preventDefault();
      });
    }
    // 2026-07-10 (Joe) — VERSION B dates wiring: any change to either date
    // or the Month-&-year shortener re-records the _pet_dates property and
    // moves the step marker (round-2: BOTH dates advance 'dates' → 'start';
    // clearing walks it back). Elements only render on version B, so this
    // is inert everywhere else. Never starts anything.
    ['date-from', 'date-to', 'dates-monthyear'].forEach(function (k) {
      var f = el(k);
      if (f) f.addEventListener('change', function () {
        // Round-2 items 4/5: the shortener also swaps the native control
        // (month wheel ↔ day calendar) — do that FIRST so everything below
        // reads the converted values.
        if (k === 'dates-monthyear') applyMonthYearMode();
        // 2026-07-10 — clear the unreadable-date message the moment the
        // fields become readable (fixed or cleared); re-shown only by the
        // Start My Art gate.
        if (!datesInputInvalid()) showDatesError(false);
        syncDateFieldUI();
        writePetNameProperty();
        updateStepFlow();
      });
    });
    // Round-2 item 3 — first tap on an EMPTY date seeds its suggested
    // starter value (got them → Jan 1 2016, passed → today) so the native
    // picker opens there instead of nowhere useful. touchstart fires before
    // iOS builds the picker sheet; focus covers desktop. The seeded value
    // is always visible in the field — nothing hidden ships to the art.
    ['date-from', 'date-to'].forEach(function (k) {
      var f = el(k);
      if (!f) return;
      var seed = function () {
        // Never seed once a generation is running or done — the prompt has
        // shipped; a value appearing NOW could not reach the art.
        if (f.disabled || f.value || _genInFlight || _completedGenId) return;
        f.value = defaultDateValue(f, k === 'date-from' ? 'from' : 'to');
        syncDateFieldUI();
        writePetNameProperty();
        updateStepFlow();
      };
      f.addEventListener('touchstart', seed, { passive: true });
      f.addEventListener('focus', seed);
    });
    // Paint the gray starter dates at load (the "to" suggestion is today —
    // client clock, so a cached page never shows a stale date).
    syncDateFieldUI();
  }

  // PREVIEW → scroll to + flash the preview box (or nudge the upload block
  // when nothing has been generated yet).
  // 2026-07-09 UI pass (Joe): on STANDALONE, pre-art this button is the
  // step-2 "Start My Art" CTA instead — it re-opens the crop (whose Done
  // auto-starts the generation, the existing chain) or resumes a
  // name-gate-blocked attempt. Post-art, and on the test arm always, the
  // original scroll-and-flash behavior runs unchanged.
  var previewBtn = el('preview-btn');
  if (previewBtn) {
    previewBtn.addEventListener('click', function () {
      if (_standalone && !_completedGenId) {
        if (_genInFlight) return;
        // 2026-07-09 v2 (Joe) — THIS button is the one and only generation
        // trigger. Order: name requirement first (error + the chip stays on
        // the Name card), then the stashed cropped photo (pre-uploaded in
        // the background → feels instant), then a crop that was never
        // confirmed (reopen the dialog; its DONE re-stashes and returns
        // here). No photo at all falls through to the upload-block nudge.
        if (_dynamicText && !nameRequirementMet()) { showNameError(true); return; }
        // 2026-07-10 — unreadable typed date (no-date-picker browsers only):
        // stop and point at the dates card; clearing or fixing it proceeds.
        if (datesInputInvalid()) { showDatesError(true); return; }
        if (_croppedFile) { emailGateThenGenerate(_croppedFile); return; }
        if (_sourceCanvas) { openCropDialog(); return; }
      }
      var target = _completedGenId ? el('preview-box') : el('upload-block');
      if (!target) return;
      try { target.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { /* ignore */ }
      target.classList.remove('wolden-flow-b--flash');
      void target.offsetWidth; // restart the animation if mid-cycle
      target.classList.add('wolden-flow-b--flash');
      setTimeout(function () { target.classList.remove('wolden-flow-b--flash'); }, 1200);
    });
  }

  // Same step name in both modes (dashboards see one Flow B funnel);
  // standalone + source_flow are the separators.
  track('flow_step_viewed', { step: 'flow_b_page', standalone: _standalone, source_flow: sourceFlow() });
  hydrateFromStore();
  resumePendingGen();
  log('[flow-b] initialized', { subVariant: sub, standalone: _standalone });
}

function onFilePicked(file) {
  clearError();
  var v = isAcceptedFile(file);
  if (!v.ok) {
    if (v.reason === 'too-large') showError('That image is over 10MB. Please choose a smaller file.');
    else showError('We accept JPG, PNG, WebP, and HEIC. Please choose one of those.');
    return;
  }
  _sourceName = file.name || 'photo';
  setFileHint('Preparing ' + _sourceName + '…');
  var adj = el('adjust-btn');
  prepareSource(file)
    .then(function (canvas) {
      _sourceCanvas = canvas;
      cropDefault();
      if (adj) adj.disabled = false;
      setFileHint(_sourceName);
      // 2026-07-09 UI pass — photo decoded: fill the chip and advance the
      // standalone step marker to 'start' (both no-ops off standalone).
      renderFileChip();
      updateStepFlow();
      openCropDialog();
    })
    ['catch'](function (err) {
      log('[flow-b] photo prepare failed', err && err.message);
      setFileHint('No Photo Selected');
      showError('We couldn’t read that photo. Try a different one (JPG, PNG, WebP, or HEIC).');
    });
}

// ─── Shared flow helpers (2026-07-01, Flow C build) ──────────────────────────
// wc77-flow-c.js imports these instead of forking them (flow-ui-spec §Common 4
// "reuse, do not fork"): the Lambda-contract name sanitizer, the email-gate
// validator, and the HEIC/EXIF-safe decode pipeline (Flow C needs it only for
// its Step-1 thumbnail — its upload path sends the ORIGINAL file, crop-free).
// Purely additive: nothing in Flow B's behavior changes.
export { sanitizeName, validEmail, isHeic, prepareSource };
